import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const VAREGRUPPER = [
    'SPORTSUTSTYR OG TILBEHØR',
    'FRITIDSBEKLEDNING',
    'FOTTØY',
    'SYKKEL',
    'VINTERSPORT',
    'JAKT - FISKE - VÅPEN',
    'FRILUFTSUTSTYR',
    'VANNSPORT',
    'EGNE GRUPPER',
];

const BUTIKKER = [
    'Sport 1 Røyken',
    'Sport 1 Slemmestad',
    'Røyken Sykkelservice',
];

function Field({ label, name, value, onChange, type = 'text', readOnly = false }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
                type={type}
                name={name}
                value={value ?? ''}
                onChange={onChange}
                readOnly={readOnly}
                className={`block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm ${
                    readOnly ? 'bg-gray-50 text-gray-500' : 'focus:outline-none focus:ring-2 focus:ring-teal-500'
                }`}
            />
        </div>
    );
}

function SelectField({ label, name, value, onChange, options }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <select
                name={name}
                value={value ?? ''}
                onChange={onChange}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
                <option value="">— Ikke valgt —</option>
                {options.map(o => <option key={o}>{o}</option>)}
            </select>
        </div>
    );
}

export default function PartChange() {
    const { id } = useParams();
    const navigate = useNavigate();
    const imageRef = useRef(null);

    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveErr, setSaveErr] = useState('');

    const [imageUploading, setImageUploading] = useState(false);
    const [imageErr, setImageErr] = useState('');
    const [sport1Preview, setSport1Preview] = useState(null);
    const [sport1Loading, setSport1Loading] = useState(false);

    useEffect(() => {
        fetch(`${API_BASE_URL}/parts/${id}`)
            .then(r => r.json())
            .then(data => setForm(data))
            .catch(() => setSaveErr('Kunne ikke hente vare.'));
    }, [id]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setSaveErr('');
        try {
            const r = await fetch(`${API_BASE_URL}/parts/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (r.ok) {
                navigate('/part-list');
            } else {
                setSaveErr('Feil ved lagring.');
            }
        } catch {
            setSaveErr('Nettverksfeil ved lagring.');
        } finally {
            setSaving(false);
        }
    }

    async function handleImageChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';
        setImageUploading(true);
        setImageErr('');
        try {
            const fd = new FormData();
            fd.append('image', file);
            const r = await fetch(`${API_BASE_URL}/parts/${id}/image`, {
                method: 'POST',
                body: fd,
            });
            if (!r.ok) throw new Error();
            const data = await r.json();
            setForm(prev => ({ ...prev, imageUrl: data.imageUrl }));
        } catch {
            setImageErr('Kunne ikke laste opp bildet. Prøv igjen.');
        } finally {
            setImageUploading(false);
        }
    }

    async function fetchFromSport1() {
        // Use brand + product name — more searchable than EAN on e-commerce sites
        const q = (form.product || '').trim();
        if (!q) { setImageErr('Fyll inn varetekst først.'); return; }
        setSport1Loading(true);
        setImageErr('');
        setSport1Preview(null);
        try {
            const r = await fetch(`${API_BASE_URL}/parts/fetch-sport1-image?q=${encodeURIComponent(q)}`);
            const data = await r.json();
            if (!r.ok) throw new Error(data.message || 'Ikke funnet');
            setSport1Preview(data.imageUrl);
        } catch (e) {
            setImageErr(e.message || 'Fant ikke bilde på Sport1.no.');
        } finally {
            setSport1Loading(false);
        }
    }

    async function confirmSport1Image() {
        if (!sport1Preview) return;
        setImageUploading(true);
        setImageErr('');
        try {
            const r = await fetch(`${API_BASE_URL}/parts/${id}/image/from-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: sport1Preview }),
            });
            if (!r.ok) throw new Error();
            const data = await r.json();
            setForm(prev => ({ ...prev, imageUrl: data.imageUrl }));
            setSport1Preview(null);
        } catch {
            setImageErr('Kunne ikke lagre bildet. Prøv manuell opplasting.');
        } finally {
            setImageUploading(false);
        }
    }

    async function handleDeleteImage() {
        if (!window.confirm('Fjern bildet fra denne varen?')) return;
        setImageUploading(true);
        setImageErr('');
        try {
            await fetch(`${API_BASE_URL}/parts/${id}/image`, { method: 'DELETE' });
            setForm(prev => ({ ...prev, imageUrl: undefined }));
        } catch {
            setImageErr('Kunne ikke slette bildet.');
        } finally {
            setImageUploading(false);
        }
    }

    return (
        <div className="max-w-2xl mx-auto py-8 px-4">
            <div className="bg-white shadow-lg rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold">Rediger vare</h2>
                    <button
                        type="button"
                        onClick={() => navigate('/part-list')}
                        className="text-sm text-gray-500 hover:text-gray-700"
                    >
                        ← Tilbake
                    </button>
                </div>

                {/* Bilde */}
                <div className="mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-2">Produktbilde</p>

                    {/* Sport1-preview modal */}
                    {sport1Preview && (
                        <div className="mb-3 p-3 border rounded-lg bg-gray-50">
                            <p className="text-xs text-gray-500 mb-2">Fant dette bildet på Sport1.no — bruk det?</p>
                            <div className="flex items-center gap-4">
                                <img src={sport1Preview} alt="Sport1 preview" className="w-24 h-24 object-contain border rounded bg-white" />
                                <div className="flex flex-col gap-2">
                                    <button
                                        type="button"
                                        onClick={confirmSport1Image}
                                        disabled={imageUploading}
                                        className="text-sm px-3 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                                    >
                                        {imageUploading ? 'Lagrer…' : 'Ja, bruk dette bildet'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSport1Preview(null)}
                                        className="text-sm px-3 py-1.5 border rounded hover:bg-gray-100"
                                    >
                                        Avbryt
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-4">
                        {/* Bilde eller placeholder */}
                        <button
                            type="button"
                            onClick={() => imageRef.current?.click()}
                            disabled={imageUploading || sport1Loading}
                            className="flex-shrink-0 flex items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition disabled:opacity-50 overflow-hidden"
                        >
                            {form.imageUrl ? (
                                <img src={form.imageUrl} alt={form.product} className="w-full h-full object-contain" />
                            ) : imageUploading ? (
                                <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <span className="text-xs text-gray-400 text-center px-2">Klikk for å laste opp</span>
                            )}
                        </button>

                        {/* Knapper */}
                        <div className="flex flex-col gap-2 pt-1">
                            <button
                                type="button"
                                onClick={fetchFromSport1}
                                disabled={sport1Loading || imageUploading}
                                className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
                            >
                                {sport1Loading ? (
                                    <><div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /> Søker…</>
                                ) : (
                                    'Hent fra Sport1.no'
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => imageRef.current?.click()}
                                disabled={imageUploading || sport1Loading}
                                className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Last opp manuelt
                            </button>
                            {form.imageUrl && (
                                <button
                                    type="button"
                                    onClick={handleDeleteImage}
                                    disabled={imageUploading}
                                    className="text-sm px-3 py-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                                >
                                    Fjern bilde
                                </button>
                            )}
                        </div>
                    </div>

                    {imageErr && <p className="text-xs text-red-600 mt-2">{imageErr}</p>}
                    <input
                        ref={imageRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageChange}
                    />
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <Field label="Varemerke" name="brand" value={form.brand} onChange={handleChange} />

                    <Field label="Varetekst" name="product" value={form.product} onChange={handleChange} />

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Farge" name="farge" value={form.farge} onChange={handleChange} />
                        <Field label="Størrelse" name="størrelse" value={form.størrelse} onChange={handleChange} />
                    </div>

                    <Field label="EAN" name="ean" value={form.ean} onChange={handleChange} />

                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Pris (kr)" name="price" value={form.price} onChange={handleChange} type="number" />
                        <Field label="Kostpris (kr)" name="kostpris" value={form.kostpris} onChange={handleChange} type="number" />
                        <Field label="Lagerantall" name="lagerantall" value={form.lagerantall} onChange={handleChange} type="number" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <SelectField label="Varegruppe" name="varegruppe" value={form.varegruppe} onChange={handleChange} options={VAREGRUPPER} />
                        <SelectField label="Butikk" name="butikk" value={form.butikk} onChange={handleChange} options={BUTIKKER} />
                    </div>

                    {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}

                    <div className="flex gap-3 pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white py-2 rounded-lg font-medium text-sm transition"
                        >
                            {saving ? 'Lagrer…' : 'Lagre endringer'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/part-list')}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                        >
                            Avbryt
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
